/**
 * Decoded frames onto the headset's screen, without copying them.
 *
 * A decoded VideoFrame is a handle to memory the decoder owns, and it has to be closed or the
 * decoder runs out of buffers within seconds. It also has to stay open until three has
 * actually uploaded it, which happens during render, not when it is handed over -- so each
 * frame is released only when the next one replaces it.
 */
import { LinearFilter, SRGBColorSpace, VideoFrameTexture } from "three";

export class FrameSink {
  readonly texture = new VideoFrameTexture();
  private waiting: VideoFrame | null = null;
  private uploaded: VideoFrame | null = null;
  width = 0;
  height = 0;
  frames = 0;
  /** The flushed frame's own timestamp, µs: its capture time on the relay's clock, for a stamped stream. */
  timestamp = 0;

  constructor() {
    this.texture.colorSpace = SRGBColorSpace;
    this.texture.generateMipmaps = false;          // 2560x720 is not a power of two
    this.texture.minFilter = LinearFilter;
    this.texture.magFilter = LinearFilter;
  }

  /** Video arrives at 30 Hz and the headset draws at 72; only the newest frame is worth keeping. */
  push(frame: VideoFrame): void {
    this.waiting?.close();
    this.waiting = frame;
  }

  /** Once per rendered frame, before rendering. True when there was something new. */
  flush(): boolean {
    const frame = this.waiting;
    if (!frame) return false;
    this.waiting = null;
    this.width = frame.displayWidth;
    this.height = frame.displayHeight;
    this.timestamp = frame.timestamp;
    this.texture.setFrame(frame);
    this.uploaded?.close();     // rendered at least once by now
    this.uploaded = frame;
    this.frames += 1;
    return true;
  }

  dispose(): void {
    this.waiting?.close();
    this.uploaded?.close();
    this.waiting = this.uploaded = null;
    this.texture.dispose();
  }
}
